import { formatDeviceId } from '@viewer/protocol';
import { useEffect, useState } from 'react';
import type { IncomingRequest } from '../../lib/session/controller';
import styles from './IncomingRequestDialog.module.css';

interface Props {
  request: IncomingRequest;
  passwordEnabled: boolean;
  onAccept(): void;
  onReject(): void;
}

function secondsLeft(at: string) {
  return Math.max(0, Math.ceil((new Date(at).getTime() - Date.now()) / 1000));
}

export function IncomingRequestDialog({ request, passwordEnabled, onAccept, onReject }: Props) {
  const deadline = request.autoAcceptAt ?? request.expiresAt;
  const [remaining, setRemaining] = useState(() => secondsLeft(deadline));

  useEffect(() => {
    const timer = setInterval(() => setRemaining(secondsLeft(deadline)), 250);
    return () => clearInterval(timer);
  }, [deadline]);

  const wants = request.access === 'control' ? 'ver e controlar a sua tela' : 'ver a sua tela (sem controlar)';
  const company = request.organization?.name ?? 'a empresa';
  const user = request.viewerUser;

  if (request.autoAcceptAt) {
    return (
      <div className={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="incoming-title">
        <div className={styles.dialog}>
          <p className={styles.eyebrow}>Acesso sem supervisão</p>
          <h2 id="incoming-title" className={styles.title}>
            {remaining > 0
              ? `Seu dispositivo será acessado por ${company} em ${remaining} ${remaining === 1 ? 'segundo' : 'segundos'}`
              : `Conectando ${company}...`}
          </h2>
          <p className={styles.deviceId}>
            {user ? `${user.name} · ${user.email}` : request.viewerName} · ID {formatDeviceId(request.viewerId)}
          </p>
          <p className={styles.text}>
            {user?.name ?? 'Um colaborador'} vai {wants}. Se ninguém responder, o acesso é liberado automaticamente.
          </p>
          <div className={styles.actions}>
            <button className={styles.reject} onClick={onReject} autoFocus>
              Recusar
            </button>
            <button className={styles.accept} onClick={onAccept}>
              Aceitar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="incoming-title">
      <div className={styles.dialog}>
        <p className={styles.eyebrow}>{user ? `Colaborador de ${company}` : 'Solicitação de conexão'}</p>
        <h2 id="incoming-title" className={styles.title}>
          {user?.name ?? request.viewerName}
        </h2>
        <p className={styles.deviceId}>
          {user ? `${user.email} · ` : ''}
          {request.viewerName} · ID {formatDeviceId(request.viewerId)}
        </p>
        <p className={styles.text}>
          {user
            ? `${user.name} tem permissão de ${company} para ${wants}. Aceite quando estiver pronto.`
            : `Este computador quer ${wants}. Só aceite se você reconhece quem está pedindo.`}
        </p>
        <div className={styles.actions}>
          <button className={styles.reject} onClick={onReject}>
            Recusar
          </button>
          <button className={styles.accept} onClick={onAccept} autoFocus>
            Aceitar
          </button>
        </div>
        <p className={styles.timer}>O pedido expira em {remaining}s</p>
        {passwordEnabled && (
          <p className={styles.timer}>Se a pessoa digitar a senha de acesso, a conexão começa sem precisar aceitar.</p>
        )}
      </div>
    </div>
  );
}
